import {
  hasAlternativelyUploaded,
  hasManufactureReadyArtwork,
  isReorderable,
  isReviewGroupLead,
  hasReviewGroup,
  isArtworkDelivered,
  hasDocumentReferenceComplete,
  isQuoteLineItem,
  hasActiveArtworkServices,
  hasOrchestration,
} from '@printdeal/business-logic';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useFeatureToggle } from '@printdeal/feature-management/react';

import { SanitizedLineItem, SanitizedOrder } from '../../../../../../types/order/Order';

import useProductDetailPageExists from '../../../../../../hooks/useProductDetailPageExists';
import useFileValidation from '../../../../../../hooks/useFileValidation';
import useProductValidation from '../../../../../../hooks/useProductValidation';

// @ts-expect-error
import { reOrderableQuote } from '../../../../../../helpers/order/Order';
// @ts-expect-error
import { getReorderLineItems } from '../../../../../../helpers/ReorderLineItemBuilder';
import { Environment } from '../../../../../../helpers/env';
import { ServiceFactory } from '../../../../../../services/ServiceFactory';

const ReasonsDisabled = {
  UNSUITABLE_PRODUCT: 'Unsuitable product',
  MISSING_PRODUCT_DETAIL_PAGE: 'Missing product detail page',
  MISSING_SELECTOR_CONFIG: 'Missing selector configuration',
  NOT_REORDERABLE: 'Cannot be reOrdered because of lineItem type or product version',
  PRODUCT_VALIDATION_ERROR: 'Product validation errored',
  PRODUCT_VALIDATION_LOADING: 'Product validation is loading',
  PRODUCT_VALIDATION_INVALID: 'Product is invalid',
  ORCHESTRATED_PRODUCT: 'Orchestrated products currently cannot be reOrdered.',
};

interface LineItemValidity {
  lineItem: SanitizedLineItem;
  enabled: boolean;
  hasValidFiles: boolean;
  hasInvalidFiles: boolean;
  reasonDisabled?: string;
}

const needsProductValidation = (
  lineItem: SanitizedLineItem,
  canV1ProductsBeReordered: boolean,
  productDetailPageExists: boolean,
) => productDetailPageExists
  && isReorderable(lineItem, canV1ProductsBeReordered, false)
  && (!hasReviewGroup(lineItem) || isReviewGroupLead(lineItem))
  && !hasOrchestration(lineItem); // Is NOT part of an orchestration group

const needsFileValidation = (
  lineItem: SanitizedLineItem,
  order: SanitizedOrder,
  productValidation: UseQueryResult,
  canV1ProductsBeReordered: boolean,
  productDetailPageExists: boolean,
) => (productDetailPageExists
  && isArtworkDelivered(lineItem)
  && (
    hasDocumentReferenceComplete(lineItem)
    || hasAlternativelyUploaded(lineItem)
    || hasManufactureReadyArtwork(lineItem)
  )
  && (!hasReviewGroup(lineItem) || isReviewGroupLead(lineItem)) // is NOT sibling
  && !hasOrchestration(lineItem) // Is NOT part of an orchestration group
  && !hasActiveArtworkServices(lineItem.id, order, Environment)
  && Boolean(lineItem.custom?.fields?.editorType)
  && isReorderable(lineItem, canV1ProductsBeReordered, false)
  && productValidation.isSuccess
  // TODO AS-1025 fix ts error below
  // @ts-expect-error
  && (productValidation?.data?.state === 'fullyResolved'));

const useReOrderLineItem = (lineItem: SanitizedLineItem, order: SanitizedOrder): LineItemValidity => {
  const { featureToggle: { isEnabled: canV1ProductsBeReordered } } = useFeatureToggle('disable_reorder_v1_products');
  const {
    contentfulPageId,
  } = lineItem.custom.fields;

  const productDetailPageExists = useProductDetailPageExists(contentfulPageId);

  // The fileValidation API-call will not be done unless needsFileValidation() is true.
  const productValidation = useProductValidation(
    lineItem,
    needsProductValidation(
      lineItem,
      canV1ProductsBeReordered,
      productDetailPageExists,
    ),
  );
  // The fileValidation API-call will not be done unless needsFileValidation() is true.
  const fileValidation = useFileValidation(
    lineItem,
    needsFileValidation(
      lineItem,
      order,
      productValidation,
      canV1ProductsBeReordered,
      productDetailPageExists,
    ),
  );

  if (!isReorderable(lineItem, canV1ProductsBeReordered, false)) {
    // The lineItem is not reOrderable because of lineItem type or product version.
    return {
      lineItem,
      enabled: false,
      hasValidFiles: false,
      hasInvalidFiles: false,
      reasonDisabled: ReasonsDisabled.NOT_REORDERABLE,
    };
  }

  if (hasOrchestration(lineItem)) {
    return {
      lineItem,
      hasValidFiles: false,
      hasInvalidFiles: false,
      enabled: false,
      reasonDisabled: ReasonsDisabled.ORCHESTRATED_PRODUCT,
    };
  }

  if (!productDetailPageExists) {
    return {
      lineItem,
      hasValidFiles: false,
      hasInvalidFiles: false,
      enabled: false,
      reasonDisabled: ReasonsDisabled.MISSING_PRODUCT_DETAIL_PAGE,
    };
  }

  if (productValidation.isLoading) {
    // Product validation is loading
    return {
      lineItem,
      enabled: false,
      hasValidFiles: false,
      hasInvalidFiles: false,
      reasonDisabled: ReasonsDisabled.PRODUCT_VALIDATION_LOADING,
    };
  }

  if (productValidation.isError) {
    // Product validation is loading
    return {
      lineItem,
      enabled: false,
      hasValidFiles: false,
      hasInvalidFiles: false,
      reasonDisabled: ReasonsDisabled.PRODUCT_VALIDATION_ERROR,
    };
  }

  if (productValidation.data?.state !== 'fullyResolved') {
    return {
      lineItem,
      enabled: false,
      hasValidFiles: false,
      hasInvalidFiles: false,
      reasonDisabled: ReasonsDisabled.PRODUCT_VALIDATION_INVALID,
    };
  }

  if (!isArtworkDelivered(lineItem)) {
    return {
      lineItem,
      enabled: true,
      hasValidFiles: false,
      hasInvalidFiles: false,
    };
  }

  const hasValidFiles = fileValidation.isSuccess
    && fileValidation?.data[lineItem.id]?.files.length > 0
    && fileValidation?.data[lineItem.id]?.valid;
  const hasInvalidFiles = fileValidation.isSuccess
    && (fileValidation?.data[lineItem.id]?.files === 0
      || fileValidation?.data[lineItem.id]?.valid === false);

  return {
    lineItem,
    enabled: true,
    hasValidFiles,
    hasInvalidFiles,
  };
};

const useOrderQuotesValidation = (lineItems: SanitizedLineItem[]) => {
  const quotesApi = ServiceFactory.createQuotesService();
  const quoteIds: string[] = [];
  lineItems.forEach((lineItem) => {
    const quoteId = lineItem.custom?.fields?.quoteId;
    if (quoteId && !quoteIds.includes(quoteId)) {
      quoteIds.push(quoteId);
    }
  });
  const quoteIdstring: string = quoteIds.join(',');
  return useQuery({
    queryKey: ['validate-quotes', quoteIdstring],
    queryFn: async () => {
      try {
        return await quotesApi!.validateQuote(quoteIdstring);
      } catch (error) {
        return [];
      }
    },
    gcTime: 1000,
    enabled: Boolean(quoteIds.length > 0),
  });
};

const useReOrder = (
  lineItems: SanitizedLineItem[],
  order: SanitizedOrder,
  fullOrderReorder?: boolean,
) => {
  if (lineItems.length === 0) {
    return {
      enabled: false,
      lineItemsValidity: [],
      validLineItems: [],
      invalidLineItems: [],
      hasValidFiles: false,
      hasInvalidFiles: false,
      hasInvalidProducts: false,
      isLoadingProductValidation: false,
    };
  }

  const lineItemsValidity = lineItems
    .filter((lineItem) => (isReviewGroupLead(lineItem) || !hasReviewGroup(lineItem)) && !isQuoteLineItem(lineItem))
    .map((lineItem) => useReOrderLineItem(lineItem, order));
  const validLineItems: SanitizedLineItem[] = [];
  const invalidLineItems: SanitizedLineItem[] = [];

  lineItemsValidity.forEach((lineItemValidity) => {
    if (lineItemValidity.enabled) {
      const lineItemsInGroup = getReorderLineItems(order, lineItemValidity.lineItem);
      validLineItems.push(...lineItemsInGroup);
    } else {
      invalidLineItems.push(lineItemValidity.lineItem);
    }
  });

  const quoteLineItemsValidity = useOrderQuotesValidation(lineItems);

  lineItems.forEach((lineItem) => {
    if (isQuoteLineItem(lineItem) && !fullOrderReorder) {
      if (reOrderableQuote(lineItem, quoteLineItemsValidity?.data)) {
        validLineItems.push(lineItem);
      } else {
        invalidLineItems.push(lineItem);
      }
    }
  });

  return {
    enabled: validLineItems.length > 0,
    lineItemsValidity,
    validLineItems,
    invalidLineItems,
    hasValidFiles: lineItemsValidity.some((lineItem) => lineItem.enabled && lineItem.hasValidFiles),
    hasInvalidFiles: lineItemsValidity.some((lineItem) => lineItem.enabled && lineItem.hasInvalidFiles),
    isLoadingProductValidation: lineItemsValidity.some(
      (lineItem) => lineItem.reasonDisabled === ReasonsDisabled.PRODUCT_VALIDATION_LOADING,
    ),
    hasInvalidProducts: lineItemsValidity.some(
      (lineItem) => lineItem.reasonDisabled === ReasonsDisabled.PRODUCT_VALIDATION_INVALID,
    ),
  };
};

export default useReOrder;
